import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileText,
  Mail,
  Phone,
  Building2,
  MapPin,
  Calendar,
  Eye,
} from "lucide-react";
import { logError } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status workflow: new → contacted → quoted → won / lost
const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "quoted", label: "Quoted" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

const STATUS_STYLES = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-amber-100 text-amber-700",
  quoted: "bg-purple-100 text-purple-700",
  won: "bg-green-100 text-green-700",
  lost: "bg-red-100 text-red-700",
};

const StatusBadge = ({ status }) => (
  <Badge className={`${STATUS_STYLES[status] || "bg-gray-100 text-gray-700"} font-medium capitalize`}>
    {status}
  </Badge>
);

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_e) {
    return iso;
  }
};

export const QuoteRequestsPanel = ({ quoteRequests, onChanged }) => {
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [updating, setUpdating] = useState(false);

  const counts = useMemo(() => {
    const c = { all: quoteRequests.length };
    for (const opt of STATUS_OPTIONS) {
      c[opt.value] = quoteRequests.filter((q) => q.status === opt.value).length;
    }
    return c;
  }, [quoteRequests]);

  const filtered = useMemo(() => {
    if (filter === "all") return quoteRequests;
    return quoteRequests.filter((q) => q.status === filter);
  }, [quoteRequests, filter]);

  const openDetail = (quote) => {
    setSelected(quote);
    setNotes(quote.admin_notes || "");
  };

  const updateQuote = async (quoteId, newStatus, adminNotes) => {
    setUpdating(true);
    try {
      await axios.patch(`${API}/admin/quote-requests/${quoteId}`, {
        status: newStatus,
        admin_notes: adminNotes,
      });
      toast.success(`Quote moved to "${newStatus}"`);
      onChanged();
      setSelected(null);
    } catch (e) {
      logError("update quote failed", e);
      toast.error("Failed to update quote");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div data-testid="quote-requests-panel">
      {/* Funnel KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        {[{ value: "all", label: "All" }, ...STATUS_OPTIONS].map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setFilter(opt.value)}
            className={`p-4 rounded-xl text-left border transition-all ${
              filter === opt.value
                ? "border-[#2D5016] bg-[#E8F5E9]"
                : "border-[#E2E8E0] bg-white hover:border-[#7CB342]"
            }`}
            data-testid={`quote-filter-${opt.value}`}
          >
            <p className="text-xs text-[#5C6B57] uppercase tracking-wider font-semibold">
              {opt.label}
            </p>
            <p className="text-2xl font-bold text-[#1A2E12] mt-1">
              {counts[opt.value] || 0}
            </p>
          </button>
        ))}
      </div>

      <Card className="bg-white border-[#E2E8E0]">
        <CardHeader>
          <CardTitle className="text-[#1A2E12] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#2D5016]" />
            Quote Requests ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-[#5C6B57]">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No quote requests in this view.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((q) => (
                <div
                  key={q.id}
                  className="border border-[#E2E8E0] rounded-xl p-4 hover:shadow-md transition-shadow"
                  data-testid={`quote-row-${q.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <h3 className="font-semibold text-[#1A2E12]">
                          {q.name}
                        </h3>
                        <span className="text-sm text-[#5C6B57]">·</span>
                        <span className="text-sm font-medium text-[#5C6B57] flex items-center gap-1">
                          <Building2 className="w-4 h-4" />
                          {q.company}
                        </span>
                        <StatusBadge status={q.status} />
                        {q.service_name && (
                          <Badge className="bg-[#E8F5E9] text-[#2D5016]">
                            {q.service_name}
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#5C6B57]">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" />
                          {q.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" />
                          {q.phone}
                        </span>
                        {q.postcode && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {q.postcode}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(q.created_at)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={q.status}
                        onValueChange={(v) =>
                          updateQuote(q.id, v, q.admin_notes || null)
                        }
                      >
                        <SelectTrigger
                          className="w-[130px] h-9"
                          data-testid={`quote-status-${q.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDetail(q)}
                        className="border-[#2D5016] text-[#2D5016]"
                        data-testid={`quote-view-${q.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      {selected && (
        <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#1A2E12] flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {selected.name} — {selected.company}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[#5C6B57] text-xs uppercase tracking-wider">Email</p>
                  <a
                    href={`mailto:${selected.email}`}
                    className="text-[#2D5016] hover:underline"
                  >
                    {selected.email}
                  </a>
                </div>
                <div>
                  <p className="text-[#5C6B57] text-xs uppercase tracking-wider">Phone</p>
                  <a
                    href={`tel:${selected.phone}`}
                    className="text-[#2D5016] hover:underline"
                  >
                    {selected.phone}
                  </a>
                </div>
                <div>
                  <p className="text-[#5C6B57] text-xs uppercase tracking-wider">Service</p>
                  <p className="text-[#1A2E12]">
                    {selected.service_name || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-[#5C6B57] text-xs uppercase tracking-wider">Postcode</p>
                  <p className="text-[#1A2E12]">{selected.postcode || "—"}</p>
                </div>
                <div>
                  <p className="text-[#5C6B57] text-xs uppercase tracking-wider">Heard from</p>
                  <p className="text-[#1A2E12]">{selected.heard_from || "—"}</p>
                </div>
                <div>
                  <p className="text-[#5C6B57] text-xs uppercase tracking-wider">Received</p>
                  <p className="text-[#1A2E12]">{formatDate(selected.created_at)}</p>
                </div>
              </div>

              {selected.message && (
                <div className="bg-[#F9FAF9] rounded-xl p-4">
                  <p className="text-xs text-[#5C6B57] uppercase tracking-wider mb-1">
                    Customer message
                  </p>
                  <p className="text-sm text-[#1A2E12] whitespace-pre-wrap">
                    {selected.message}
                  </p>
                </div>
              )}

              <div>
                <p className="text-xs text-[#5C6B57] uppercase tracking-wider mb-2">
                  Admin notes
                </p>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Internal notes — e.g. quote amount, call summary, follow-up date..."
                  data-testid="quote-admin-notes"
                />
              </div>

              {selected.updated_by && (
                <p className="text-xs text-[#5C6B57]">
                  Last updated by {selected.updated_by} on{" "}
                  {formatDate(selected.updated_at)}
                </p>
              )}

              <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E2E8E0]">
                {STATUS_OPTIONS.map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={selected.status === opt.value ? "default" : "outline"}
                    disabled={updating}
                    onClick={() => updateQuote(selected.id, opt.value, notes)}
                    className={
                      selected.status === opt.value
                        ? "bg-[#2D5016] hover:bg-[#1A2E12] text-white"
                        : "border-[#2D5016] text-[#2D5016]"
                    }
                    data-testid={`quote-set-${opt.value}`}
                  >
                    Mark as {opt.label}
                  </Button>
                ))}
              </div>

              <Button
                onClick={() =>
                  updateQuote(selected.id, selected.status, notes)
                }
                disabled={updating}
                className="w-full bg-[#7CB342] hover:bg-[#689F38] text-white rounded-full"
                data-testid="quote-save-notes"
              >
                Save notes
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
